import { createSelector, createSlice, PayloadAction, SerializedError } from "@reduxjs/toolkit";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import Guid from "_shared/guids/guid";
import Session from "users/__session/session";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import { proposalsApi } from "work/proposals/_api/proposal-api";
import ProposalQuerySchema, { ProposalQueryJSON } from "work/proposals/_api/queries/_schemas/proposal-query-schema";
import ProposalQuery from "work/proposals/_api/queries/proposal-query";
import BarebonesProposalResponse from "work/proposals/_api/responses/barebones-proposal-response";
import ProposalBuilderSchema, { ProposalBuilderJSON } from "work/proposals/_schemas/proposal-builder-schema";
import ProposalSchema, { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import Proposal from "work/proposals/proposal";

export type ProposalLoadIssue = { id: Guid; issue: string };
export const isProposalLoadIssue = (value: object): value is ProposalLoadIssue => {
  return "issue" in value;
};
export type ProposalStoreState = {
  byId: Record<string, ProposalJSON | null | undefined>;
  builders: {
    original: Record<string, ProposalBuilderJSON | undefined>;
    updated: Record<string, ProposalBuilderJSON | undefined>;
  };
  bareBones: Record<string, BarebonesProposalResponse | undefined>;
  byQuery: Record<string, PaginatedResponse<ProposalJSON | ProposalLoadIssue>>;
  interfaceQueries: {
    entries: Record<string, ProposalQueryJSON>;
    lastSuccessfulResponses: Record<string, PaginatedResponse<ProposalJSON | ProposalLoadIssue>>;
  };
};
const initialState: ProposalStoreState = {
  byId: {},
  builders: {
    original: {},
    updated: {},
  },
  bareBones: {
    entries: {},
    loading: {},
  },
  byQuery: {},
  interfaceQueries: {
    entries: {},
    lastSuccessfulResponses: {},
  },
};

const proposalsSlice = createSlice({
  name: "proposals",
  initialState,
  reducers: {
    addProposal: (state, action: PayloadAction<Proposal>) => {
      const proposal = action.payload;
      if (!proposal.id) {
        console.error("Proposal does not have an id");
        return;
      }

      state.byId[proposal.id.value] = proposal.toJSON();
    },
    addProposalJSON: (state, action: PayloadAction<ProposalJSON>) => {
      const proposalJSON = action.payload;
      if (!proposalJSON.id) {
        console.error("Proposal does not have an id");
        return;
      }
      state.byId[proposalJSON.id] = proposalJSON;
    },
    updateProposal: (state, action: PayloadAction<Proposal>) => {
      const proposal = action.payload;
      if (!proposal.id) {
        console.error("Proposal does not have an id");
        return;
      }
      const proposalJSON = proposal.toJSON();
      state.byId[proposal.id.value] = proposalJSON;
    },
    removeProposal: (state, action: PayloadAction<Guid>) => {
      state.byId[action.payload.value] = null;
      state.builders.original[action.payload.value] = undefined;
      state.builders.updated[action.payload.value] = undefined;
    },
    clearNewProposalBuilder: (state) => {
      state.builders.original[Guid.EMPTY.value] = undefined;
      state.builders.updated[Guid.EMPTY.value] = undefined;
    },
    replaceUpdatedProposalBuilder: (state, action: PayloadAction<{ proposalId: Guid; builder?: ProposalBuilder }>) => {
      const proposalIdValue = action.payload.proposalId.value;
      const builderJSON = action.payload.builder?.toJSON();
      state.builders.updated[proposalIdValue] = builderJSON;
    },
    replaceOriginalProposalBuilder: (
      state,
      action: PayloadAction<{
        proposalId: Guid;
        builder: ProposalBuilder | undefined;
      }>,
    ) => {
      const proposalIdValue = action.payload.proposalId.value;
      const builderJSON = action.payload.builder?.toJSON();
      state.builders.original[proposalIdValue] = builderJSON;
    },
    resetBuilderByProposalId: (
      state,
      action: PayloadAction<{
        id: Guid;
        session: Session;
      }>,
    ) => {
      const { id } = action.payload;
      const json = state.builders.updated[id.value];
      if (!json) return;
      state.builders.updated[id.value] = state.builders.original[id.value];
    },
    updateProposalInterfaceQuery: (
      state,
      action: PayloadAction<{
        interfaceName: string;
        query: ProposalQuery;
      }>,
    ) => {
      const { interfaceName, query } = action.payload;
      state.interfaceQueries.entries[interfaceName] = ProposalQuerySchema.serialize(query);
    },
    resetProposalSessionByProposalId: (
      state,
      action: PayloadAction<{
        id: Guid;
      }>,
    ) => {
      const { id } = action.payload;
      state.builders.updated[id.value] = state.builders.original[id.value];
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(proposalsApi.endpoints.getProposalsByIds.matchFulfilled, (state, action) => {
      for (const proposal of action.payload) {
        if (!proposal.id) {
          console.error("Proposal does not have an id", proposal);
          continue;
        }
        state.bareBones[proposal.id] = proposal;
      }
    });

    builder.addMatcher(proposalsApi.endpoints.submitProposal.matchFulfilled, (state, action) => {
      const proposalJSON = action.payload;
      if (!proposalJSON.id) {
        console.error("Proposal does not have an id", proposalJSON);
        return;
      }
      state.builders.original[proposalJSON.id] = undefined;
      state.builders.updated[proposalJSON.id] = undefined;
      state.byId[proposalJSON.id] = proposalJSON;
    });

    builder.addMatcher(proposalsApi.endpoints.requestProposalRevision.matchFulfilled, (state, action) => {
      const proposalJSON = action.payload;
      if (!proposalJSON.id) {
        console.error("Proposal does not have an id", proposalJSON);
        return;
      }
      state.builders.original[proposalJSON.id] = undefined;
      state.builders.updated[proposalJSON.id] = undefined;
      state.byId[proposalJSON.id] = proposalJSON;
    });

    builder.addMatcher(proposalsApi.endpoints.approveProposal.matchFulfilled, (state, action) => {
      const proposalJSON = action.payload;
      if (!proposalJSON.id) {
        console.error("Proposal does not have an id", proposalJSON);
        return;
      }
      state.byId[proposalJSON.id] = proposalJSON;
    });

    builder.addMatcher(proposalsApi.endpoints.hireProposal.matchFulfilled, (state, action) => {
      const proposalJSON = action.payload;
      if (!proposalJSON.id) {
        console.error("Proposal does not have an id", proposalJSON);
        return;
      }
      state.byId[proposalJSON.id] = proposalJSON;
    });

    builder.addMatcher(proposalsApi.endpoints.shareProposal.matchFulfilled, (state, action) => {
      const proposalJSON = action.payload;
      if (!proposalJSON.id) {
        console.error("Proposal does not have an id", proposalJSON);
        return;
      }
      state.byId[proposalJSON.id] = proposalJSON;
      state.builders.original[proposalJSON.id] = undefined;
      state.builders.updated[proposalJSON.id] = undefined;
    });

    builder.addMatcher(proposalsApi.endpoints.rejectProposal.matchFulfilled, (state, action) => {
      const proposalJSON = action.payload;
      if (!proposalJSON.id) {
        console.error("Proposal does not have an id", proposalJSON);
        return;
      }
      state.byId[proposalJSON.id] = proposalJSON;
    });

    builder.addMatcher(proposalsApi.endpoints.cancelProposal.matchFulfilled, (state, action) => {
      const proposalJSON = action.payload;
      if (!proposalJSON.id) {
        console.error("Proposal does not have an id", proposalJSON);
        return;
      }
      state.byId[proposalJSON.id] = proposalJSON;
    });

    builder.addMatcher(proposalsApi.endpoints.deleteProposal.matchFulfilled, (state, action) => {
      const proposalIdValue = action.meta.arg.originalArgs.proposalId;
      if (!proposalIdValue) {
        console.error("Proposal does not have an id", proposalIdValue);
        return;
      }
      state.byId[proposalIdValue] = null;
    });
    builder.addMatcher(proposalsApi.endpoints.bulkDeleteProposals.matchFulfilled, (state, action) => {
      for (const proposalIdValue of action.meta.arg.originalArgs.ids) {
        state.byId[proposalIdValue] = null;
      }
    });

    builder.addMatcher(proposalsApi.endpoints.getProposals.matchFulfilled, (state, action) => {
      if (!action.meta.arg.originalArgs) return;
      const { query, interfaceName } = action.meta.arg.originalArgs;
      const responseJson = action.payload;
      if (!query || !responseJson) return;
      const queryKey = query.cacheKey;

      const validatedProposalData: (ProposalJSON | ProposalLoadIssue)[] = [];
      const proposalBuilders: Record<string, ProposalBuilderJSON> = {};

      for (const data of responseJson.data) {
        const proposalJSON: ProposalJSON = data;

        let id = Guid.EMPTY;
        try {
          id = Guid.fromJSON(proposalJSON.id) ?? id;
          if (ProposalSchema.validateJSON(proposalJSON)) {
            validatedProposalData.push(proposalJSON);
          }
          let proposalBuilderJSON: ProposalBuilderJSON;
          if (typeof proposalJSON.userBuilderJSON === "string") {
            proposalBuilderJSON = JSON.parse(proposalJSON.userBuilderJSON);
            proposalBuilderJSON = ProposalBuilderSchema.ProposalBuilderJSONZ.parse(proposalBuilderJSON);
            proposalBuilders[id.value] = proposalBuilderJSON;
          } else if (proposalJSON.userBuilderJSON) {
            proposalBuilderJSON = ProposalBuilderSchema.ProposalBuilderJSONZ.parse(proposalJSON.userBuilderJSON);
            proposalBuilders[id.value] = proposalBuilderJSON;
          }
        } catch (error) {
          validatedProposalData.push({
            id,
            issue: "Error loading Proposal.",
          });
          console.error("Error validating Proposal JSON:", error);
        }
      }

      const paginatedResponse = PaginatedResponse.from<ProposalJSON | ProposalLoadIssue>(
        validatedProposalData,
        responseJson.headers,
      );

      state.byQuery[queryKey] = paginatedResponse;
      if (interfaceName) {
        // Update last successful response for this interface
        state.interfaceQueries.lastSuccessfulResponses[interfaceName] = paginatedResponse;
      }

      const session = Session.loadFromStorage(() => {});
      for (const proposalData of paginatedResponse.data) {
        if (!proposalData.id) {
          console.error("Failed to deserialize incoming proposal", proposalData);
          continue;
        }
        if (isProposalLoadIssue(proposalData)) {
          continue;
        }
        const incomingProposal: Proposal | null | undefined = Proposal.fromJSON({
          json: proposalData,
          currentUser: session.user!,
          options: { createNewRedline: true },
        });
        if (!incomingProposal?.id) {
          console.error("Failed to deserialize incoming proposal", proposalData);
          continue;
        }

        const existingProposalJSON = state.byId[incomingProposal.id.value];
        const existingProposal: Proposal | null | undefined = Proposal.fromJSON({
          json: existingProposalJSON,
          currentUser: session.user!,
          options: { createNewRedline: true },
        });
        if (existingProposal?.lastUpdated?.isAfter(incomingProposal.lastUpdated)) {
          continue;
        }

        state.byId[incomingProposal.id.value] = proposalData;

        if (!incomingProposal.isSubmitted) {
          const incomingBuilder: ProposalBuilder | null | undefined = ProposalBuilder.fromJSON({
            json: proposalBuilders[incomingProposal.id.value],
            session,
          });

          const existingUpdatedBuilderJSON = state.builders.updated[incomingProposal.id.value];
          const existingUpdatedBuilder = ProposalBuilder.fromJSON({
            json: existingUpdatedBuilderJSON,
            session,
          });

          const existingOriginalBuilderJSON = state.builders.original[incomingProposal.id.value];
          const existingOriginalBuilder = ProposalBuilder.fromJSON({
            json: existingOriginalBuilderJSON,
            session,
          });

          let originalBuilder = existingOriginalBuilder;
          let updatedBuilder = existingUpdatedBuilder;
          if (!updatedBuilder || incomingBuilder?.lastUpdated.isAfter(updatedBuilder.lastUpdated)) {
            originalBuilder = incomingBuilder;
            updatedBuilder = incomingBuilder;
          }

          if (updatedBuilder && incomingProposal.lastUpdated?.isAfter(updatedBuilder.lastUpdated)) {
            updatedBuilder = updatedBuilder.mergeUpdatedSpec(incomingProposal.spec);
          }
          state.builders.original[incomingProposal.id.value] = originalBuilder?.toJSON();
          state.builders.updated[incomingProposal.id.value] = updatedBuilder?.toJSON();
        }
      }
    });

    builder.addMatcher(proposalsApi.endpoints.getProposalById.matchFulfilled, (state, action) => {
      if (action.payload === null) {
        state.byId[action.meta.arg.originalArgs.proposalId] = null;
        return;
      }
      const session: Session = action.meta.arg.originalArgs.session;
      const incomingProposalJSON: ProposalJSON = action.payload;
      const incomingProposal: Proposal | null | undefined = Proposal.fromJSON({
        json: incomingProposalJSON,
        currentUser: session.user!,
        options: { createNewRedline: true },
      });
      if (!incomingProposal?.id) {
        console.error("Failed to deserialize incoming proposal", incomingProposalJSON);
        return;
      }
      const existingProposalJSON = state.byId[incomingProposal.id.value];
      const existingProposal: Proposal | null | undefined = Proposal.fromJSON({
        json: existingProposalJSON,
        currentUser: session.user!,
        options: { createNewRedline: true },
      });
      if (existingProposal?.lastUpdated?.isAfter(incomingProposal.lastUpdated)) {
        return;
      }
      state.byId[incomingProposal.id.value] = incomingProposalJSON;

      if (!incomingProposal.isSubmitted) {
        let incomingProposalBuilderJSON: ProposalBuilderJSON | undefined = undefined;
        if (typeof incomingProposalJSON.userBuilderJSON === "string") {
          incomingProposalBuilderJSON = JSON.parse(incomingProposalJSON.userBuilderJSON);
          incomingProposalBuilderJSON = ProposalBuilderSchema.ProposalBuilderJSONZ.parse(incomingProposalBuilderJSON);
        } else if (incomingProposalJSON.userBuilderJSON) {
          incomingProposalBuilderJSON = ProposalBuilderSchema.ProposalBuilderJSONZ.parse(
            incomingProposalJSON.userBuilderJSON,
          );
        }
        const incomingBuilder: ProposalBuilder | null | undefined = ProposalBuilder.fromJSON({
          json: incomingProposalBuilderJSON,
          session,
        });

        const existingUpdatedBuilderJSON = state.builders.updated[incomingProposal.id.value];
        const existingUpdatedBuilder = ProposalBuilder.fromJSON({
          json: existingUpdatedBuilderJSON,
          session,
        });

        const existingOriginalBuilderJSON = state.builders.original[incomingProposal.id.value];
        const existingOriginalBuilder = ProposalBuilder.fromJSON({
          json: existingOriginalBuilderJSON,
          session,
        });

        let originalBuilder = existingOriginalBuilder;
        let updatedBuilder = existingUpdatedBuilder;
        if (!updatedBuilder || incomingBuilder?.lastUpdated.isAfter(updatedBuilder.lastUpdated)) {
          originalBuilder = incomingBuilder;
          updatedBuilder = incomingBuilder;
        }

        if (updatedBuilder && incomingProposal.lastUpdated?.isAfter(updatedBuilder.lastUpdated)) {
          updatedBuilder = updatedBuilder.mergeUpdatedSpec(incomingProposal.spec);
        }
        state.builders.original[incomingProposal.id.value] = originalBuilder?.toJSON();
        state.builders.updated[incomingProposal.id.value] = updatedBuilder?.toJSON();
      }
    });

    builder.addMatcher(proposalsApi.endpoints.saveProposal.matchFulfilled, (state, action) => {
      const proposalIdValue = action.payload.id;
      if (!proposalIdValue) {
        console.error("Proposal does not have an id", action.payload);
        return;
      }

      const builderJSON =
        typeof action.payload.userBuilderJSON === "string"
          ? JSON.parse(action.payload.userBuilderJSON)
          : action.payload.userBuilderJSON;

      state.builders.original[proposalIdValue] = builderJSON;
      state.builders.updated[proposalIdValue] = builderJSON;
      state.byId[proposalIdValue] = action.payload;
    });
  },
});

export const {
  addProposal,
  addProposalJSON,
  updateProposal,
  removeProposal,
  clearNewProposalBuilder,
  replaceUpdatedProposalBuilder,
  replaceOriginalProposalBuilder,
  resetBuilderByProposalId,
  resetProposalSessionByProposalId,
  updateProposalInterfaceQuery,
} = proposalsSlice.actions;

const selectProposalIdValue = createSelector(
  [
    (state: { proposals: ProposalStoreState }, idValue: string | undefined) => state.proposals.byId[idValue ?? ""],
    (_state: { proposals: ProposalStoreState }, _idValue: string | undefined) => undefined,
  ],
  (proposalEntryJSON: ProposalJSON | null | undefined): ProposalJSON | undefined => {
    if (!proposalEntryJSON) return;
    return proposalEntryJSON;
  },
);
export const getProposalById = (id: Guid | undefined) => (state: { proposals: ProposalStoreState }) =>
  selectProposalIdValue(state, id?.value);

const selectOriginalBuilderByProposalId = createSelector(
  [
    (state: { proposals: ProposalStoreState }) => state.proposals.builders.original,
    (_: { proposals: ProposalStoreState }, proposalId?: Guid) => proposalId,
  ],
  (originalBuilderEntries, proposalId) => {
    if (!proposalId) return;
    const builderJson = originalBuilderEntries[proposalId.value];
    if (!builderJson) return;
    return builderJson;
  },
);

const selectUpdatedBuilderByProposalId = createSelector(
  [
    (state: { proposals: ProposalStoreState }) => state.proposals.builders.updated,
    (_: { proposals: ProposalStoreState }, proposalId?: Guid) => proposalId,
  ],
  (updatedBuilderEntries, proposalId) => {
    if (!proposalId) return;
    const builderJson = updatedBuilderEntries[proposalId.value];
    if (!builderJson) return;
    return builderJson;
  },
);

export const getUpdatedProposalBuilderByProposalId =
  (proposalId?: Guid) => (state: { proposals: ProposalStoreState }) =>
    selectUpdatedBuilderByProposalId(state, proposalId);

export const getOriginalProposalBuilderByProposalId =
  (proposalId?: Guid) => (state: { proposals: ProposalStoreState }) =>
    selectOriginalBuilderByProposalId(state, proposalId);

const selectProposalQueryByInterface = createSelector(
  [
    (state: { proposals: ProposalStoreState }) => state.proposals.interfaceQueries.entries,
    (_: { proposals: ProposalStoreState }, interfaceName: string) => interfaceName,
  ],
  (queries, interfaceName) => {
    const queryJson = queries[interfaceName];
    if (!queryJson) return;
    return ProposalQuery.fromJSON(queryJson);
  },
);
export const getProposalQueryByInterface = (interfaceName: string) => (state: { proposals: ProposalStoreState }) =>
  selectProposalQueryByInterface(state, interfaceName);

const selectProposalsByQueryKey = createSelector(
  [
    (state: { proposals: ProposalStoreState }, queryKey: string | undefined) => state.proposals.byQuery[queryKey ?? ""],
    (_state: { proposals: ProposalStoreState }, _queryKey: string | undefined, query: ProposalQuery | undefined) =>
      query,
  ],
  (
    paginatedProposalJSONEntry: PaginatedResponse<ProposalJSON | ProposalLoadIssue> | undefined,
  ): PaginatedResponse<ProposalJSON | ProposalLoadIssue> | undefined => {
    if (!paginatedProposalJSONEntry) return;
    return paginatedProposalJSONEntry;
  },
);

export const getProposalsByQuery = (query: ProposalQuery | undefined) => (state: { proposals: ProposalStoreState }) =>
  selectProposalsByQueryKey(state, query?.cacheKey, query);

const selectLastSuccessfulProposalQueryByInterface = createSelector(
  [
    (state: { proposals: ProposalStoreState }, interfaceName: string) =>
      state.proposals.interfaceQueries.lastSuccessfulResponses[interfaceName],
    (_state: { proposals: ProposalStoreState }, _interfaceName: string) => undefined,
  ],
  (
    paginatedProposalJSONEntry: PaginatedResponse<ProposalJSON | ProposalLoadIssue> | undefined,
  ): PaginatedResponse<ProposalJSON | ProposalLoadIssue> | undefined => {
    if (!paginatedProposalJSONEntry) return;
    return paginatedProposalJSONEntry;
  },
);
export const getLastSuccessfulProposalQueryByInterface =
  (interfaceName: string) => (state: { proposals: ProposalStoreState }) => {
    return selectLastSuccessfulProposalQueryByInterface(state, interfaceName);
  };

const selectProposalsByIds = createSelector(
  [
    (state: { proposals: ProposalStoreState }) => state.proposals.bareBones,
    (_: { proposals: ProposalStoreState }, ids: Guid[]) => ids,
  ],
  (entries, ids) => {
    return ids.map((id) => entries[id.value]).filter((proposal) => proposal !== undefined);
  },
);
export const getProposalsByIds = (ids: Guid[]) => (state: { proposals: ProposalStoreState }) => {
  return selectProposalsByIds(state, ids);
};

export type ProposalError = {
  id: Guid;
  error: SerializedError;
};

export default proposalsSlice;
